import { useCallback, useEffect } from "react";
import { AuthService } from "@/services/Auth";
import { LoginArgs } from "@/services/Auth/types";
import LocalStorageService from "@/services/LocalStorage/LocalStorageService";
import { UserService } from "@/services/User";
import { authActions } from "@/store/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@/store/store";
import { mainBackendApi } from "@/services";
import {
	LSKeyAccessToken,
	LSKeyAccessTokenExp,
	LSKeyRefreshToken,
} from "@/lib/LocalStorageConstants";
import { Token } from "@/services/Auth/types";

/**
 * Converts expiresIn to an absolute Unix timestamp (seconds).
 * If the value looks like a relative duration (< 1 billion), it's treated
 * as "seconds from now". Otherwise it's already an absolute timestamp.
 */
const toAbsoluteExp = (expiresIn: number | undefined): number => {
	if (!expiresIn) return Math.floor(Date.now() / 1000) + 3600;
	if (expiresIn < 1_000_000_000) return Math.floor(Date.now() / 1000) + expiresIn;
	return expiresIn;
};

export default function useAuth() {
	const dispatch = useAppDispatch();

	const isAuth = useAppSelector((state) => state.auth.isAuth);

	const {
		data,
		isError: isUserError,
		isFetching: isUserLoading,
	} = UserService.useMeQuery(undefined, {
		skip: !isAuth,
	});

	const user = data ?? null;

	const [loginQuery, { isFetching: isLoginLoading }] =
		AuthService.useLazyLoginQuery();

	const [refreshQuery] = AuthService.useLazyRefreshQuery();
	const [logoutQuery] = AuthService.useLazyLogoutQuery();

	const checkAccessResponse = useCallback(
		(data: Token) => {
			if (data && data.accessToken) {
				LocalStorageService.set(LSKeyAccessToken, data.accessToken);
				LocalStorageService.set(LSKeyAccessTokenExp, toAbsoluteExp(data.expiresIn));
				LocalStorageService.set(LSKeyRefreshToken, data.refreshToken);

				dispatch(authActions.setAuth(true));

				if (!user) {
					dispatch(UserService.util.invalidateTags(["User"]));
				}
			}
		},
		[user, dispatch],
	);

	const login = useCallback(
		async (loginArgs: LoginArgs) => {
			const result = await loginQuery(loginArgs).unwrap();

			if (result) {
				if (
					"accessToken" in result &&
					typeof result.accessToken === "string"
				) {
					const token = result;
					checkAccessResponse(token);
					return;
				}
			}

			dispatch(
				authActions.setCredentials({
					login: "",
					password: "",
				}),
			);

			dispatch(authActions.setAuthEmail(loginArgs.login));

			return result;
		},
		[checkAccessResponse, loginQuery, dispatch],
	);

	const logout = useCallback(async () => {
		try {
			await logoutQuery().unwrap();
		} catch {
			// ignore — clear local state regardless
		}

		LocalStorageService.remove(LSKeyAccessToken);
		LocalStorageService.remove(LSKeyRefreshToken);
		LocalStorageService.remove(LSKeyAccessTokenExp);

		dispatch(authActions.setAuth(false));
		dispatch(mainBackendApi.util.resetApiState());
	}, [logoutQuery, dispatch]);

	const checkAuth = useCallback(async () => {
		dispatch(authActions.setLoading(true));
		try {
			const accessTokenExp =
				LocalStorageService.get(LSKeyAccessTokenExp) * 1000;
			const currentDateExp: number = new Date().getTime();

			if (accessTokenExp > currentDateExp) {
				dispatch(authActions.setAuth(true));

				if (!user) {
					dispatch(UserService.util.invalidateTags(["User"]));
				}
				return;
			}

			const refreshToken = LocalStorageService.get(LSKeyRefreshToken);
			if (!refreshToken) return;

			const result = await refreshQuery(refreshToken).unwrap();

			if (result) {
				checkAccessResponse(result);
			}
		} catch (error) {
			console.error("Auth check failed:", error);
		} finally {
			dispatch(authActions.setLoading(false));
		}
	}, [checkAccessResponse, refreshQuery, user, dispatch]);

	useEffect(() => {
		if (isUserError) {
			logout();
		}
	}, [isUserError, logout]);

	return {
		user,
		isUserLoading,
		login,
		isLoginLoading,
		logout,
		checkAuth,
		checkAccessResponse,
	};
}
